import { ActionModal } from '@kcftech/design-system';
import {
    ApplicationRoleCode,
    CorporationUserRole,
    ICorporationResponse,
    ILocationResponse,
    LocationUserRole,
    UserCreateRequest,
    UserRequestCorporationChild,
    UserRequestLocationChild,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { Checkbox } from 'components/forms/Checkbox';
import { Label } from 'components/forms/Label';
import { Select } from 'components/forms/Select';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useValidUserRoleOptions } from 'custom_hooks/data_hooks/user/useValidUserRoleOptions';
import { useToggle } from 'custom_hooks/useToggle';
import { sdUserEmailRules } from 'pages/Login/shared';
import { useMemo } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { SecondaryText } from 'styles/theme_styles/text';
import { useDebounceOnChangeValidation } from 'utils/form';

const firstNameRules: RegisterOptions = {
    required: 'Please enter the first name',
    maxLength: {
        value: 50,
        message: 'First name is too long',
    },
};

const lastNameRules: RegisterOptions = {
    required: 'Please enter the last name',
    maxLength: {
        value: 50,
        message: 'Last name is too long',
    },
};

const applicationRoleCodeRules: RegisterOptions = {
    required: 'Role required',
};

export type AddUsersModalProps = Readonly<{
    onCreate: () => void;
    onClose: () => void;
    corporation?: ICorporationResponse;
    location?: ILocationResponse;
}>;

type AddUsersForm = Readonly<{
    FirstName: string;
    LastName: string;
    Email: string;
    ApplicationRoleCode: ApplicationRoleCode;
}>;

const getCorporationUserRole = (
    location: ILocationResponse | undefined,
    isAdmin: boolean,
) => {
    if (location) {
        return CorporationUserRole.None;
    }
    return isAdmin ? CorporationUserRole.Admin : CorporationUserRole.User;
};

export function CreateUsersModal({
    corporation,
    location,
    onCreate,
    onClose,
}: AddUsersModalProps) {
    const { showToast } = useNotificationContext();

    const {
        register,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
        watch,
    } = useForm<AddUsersForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const { ApplicationRoleCode: selectedUserRole } = watch();
    const [admin, toggleAdmin] = useToggle();

    const message = useMemo(() => {
        const baseMessage = 'User will be added to';
        if (corporation && location) {
            return `${baseMessage} ${corporation.Name} / ${location.Name}`;
        }
        if (corporation) {
            return `${baseMessage} ${corporation.Name}`;
        }
        if (location) {
            return `${baseMessage} ${location.Name}`;
        }
        return undefined;
    }, [corporation, location]);

    const isLocationOrCorporationUser =
        selectedUserRole === ApplicationRoleCode.User && (corporation || location);

    const userRoleOptions = useValidUserRoleOptions();
    const filteredUserRoleOptions = userRoleOptions.filter(
        (role) => role.value === ApplicationRoleCode.User,
    );

    const { mutate: addUser } = useMutation({
        meta: { name: 'create-user' },
        mutationFn: async (data: AddUsersForm) => {
            const request = new UserCreateRequest({
                ...data,
                Corporations:
                    corporation && selectedUserRole === ApplicationRoleCode.User
                        ? [
                              new UserRequestCorporationChild({
                                  CorporationId: corporation.Id,
                                  CorporationUserRole: getCorporationUserRole(
                                      location,
                                      admin,
                                  ),
                              }),
                          ]
                        : undefined,
                Locations:
                    location && selectedUserRole === ApplicationRoleCode.User
                        ? [
                              new UserRequestLocationChild({
                                  LocationId: location.Id,
                                  LocationUserRole: admin
                                      ? LocationUserRole.Admin
                                      : LocationUserRole.User,
                              }),
                          ]
                        : undefined,
            });
            await sdClient.createUser({ body: request });
        },

        onSuccess: () => {
            onCreate();
            showToast({ type: 'success', content: 'Successfully added user' });
        },

        onError: () => showToast({ type: 'error', content: 'Failed to add user' }),
    });

    const checkEmailExist = async (newEmail: string) => {
        if (await sdClient.checkEmailExists({ email: newEmail })) {
            return 'Email already exists, please enter a unique email';
        }
        return undefined;
    };

    const emailFormProps = useDebounceOnChangeValidation(
        register('Email', {
            ...sdUserEmailRules,
            validate: async (value: string) =>
                value ? checkEmailExist(value) : undefined,
        }),
    );

    const onAddUser = (formData: AddUsersForm) => addUser(formData);

    return (
        <ActionModal
            onAction={handleSubmit(onAddUser)}
            actionable={(!isSubmitted || isValid) && !(isValidating || isSubmitting)}
            onClose={onClose}
            title="Add User"
            actionButtonText="Add"
            closeButtonText="Cancel"
        >
            <ValidatableLabel
                label="First Name"
                error={errors.FirstName}
                required
                className="mb-20"
            >
                <TextInput
                    autoFocus
                    placeholder="Enter first name"
                    invalid={errors.FirstName !== undefined}
                    {...register('FirstName', firstNameRules)}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Last Name"
                error={errors.LastName}
                required
                className="mb-20"
            >
                <TextInput
                    placeholder="Enter last name"
                    invalid={errors.LastName !== undefined}
                    {...register('LastName', lastNameRules)}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Email Address"
                error={errors.Email}
                required
                className="mb-20"
            >
                <TextInput
                    type="email"
                    placeholder="Enter email address"
                    invalid={errors.Email !== undefined}
                    {...emailFormProps}
                />
            </ValidatableLabel>
            <ValidatableLabel label="Role" error={errors.ApplicationRoleCode} required>
                <Select
                    className="mb-10"
                    options={
                        corporation || location
                            ? filteredUserRoleOptions
                            : userRoleOptions
                    }
                    value={selectedUserRole}
                    {...register('ApplicationRoleCode', applicationRoleCodeRules)}
                />
                {isLocationOrCorporationUser && message && (
                    <SecondaryText>{message}</SecondaryText>
                )}
                <div className="mb-10" />
            </ValidatableLabel>
            {isLocationOrCorporationUser && (
                <Label label="Admin">
                    <Checkbox checked={admin} onChange={toggleAdmin} />
                </Label>
            )}
        </ActionModal>
    );
}
